import React, { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate, formatError } from "@/lib/api";
import { SectionTitle, Btn, Modal, Field, SelectField, TextArea, EmptyState, Badge, SearchBar, normalize } from "@/components/Brutal";
import CooperadoCombobox from "@/components/CooperadoCombobox";
import ProductCombobox from "@/components/ProductCombobox";
import { openWhatsapp, formatCommunityPurchaseMessage } from "@/lib/whatsapp";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, ShoppingBasket, Printer, Trash2, ChevronRight, ChevronDown, X as XIcon, UserPlus, MessageCircle } from "lucide-react";

const emptyItem = () => ({ product_id: "", quantidade: 0, valor_unitario: 0 });
const emptyBatch = { cooperado_id: "", nota_fiscal: "", observacoes: "", forma_pagamento: "PIX", itens: [emptyItem()] };
const emptyPerson = { nome: "", cpf: "", telefone: "", community_id: "", tipo: "comunitario", funcao: "" };

export default function CommunityPurchases() {
  const { hasRole } = useAuth();
  const canEdit = hasRole("almoxarife");
  const [groups, setGroups] = useState([]);
  const [cooperados, setCooperados] = useState([]);
  const [communities, setCommunities] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [batch, setBatch] = useState(emptyBatch);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [personTipoFilter, setPersonTipoFilter] = useState("all");
  const [listTipoFilter, setListTipoFilter] = useState("all");
  const [newPersonOpen, setNewPersonOpen] = useState(false);
  const [personForm, setPersonForm] = useState(emptyPerson);
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    const [g, co, cm, p] = await Promise.all([
      api.get("/community-purchases/grouped"),
      api.get("/cooperados"),
      api.get("/communities"),
      api.get("/products"),
    ]);
    setGroups(g.data); setCooperados(co.data); setCommunities(cm.data); setProducts(p.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const addItem = () => setBatch((b) => ({ ...b, itens: [...b.itens, emptyItem()] }));
  const setItem = (i, k, v) => setBatch((b) => { const cp = [...b.itens]; cp[i] = { ...cp[i], [k]: v }; return { ...b, itens: cp }; });
  const removeItem = (i) => setBatch((b) => ({ ...b, itens: b.itens.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      const cleanItens = batch.itens
        .filter((it) => it.product_id && parseFloat(it.quantidade) > 0)
        .map((it) => ({ product_id: it.product_id, quantidade: parseFloat(it.quantidade), valor_unitario: parseFloat(it.valor_unitario) || 0 }));
      if (cleanItens.length === 0) { toast.error("Adicione ao menos um item válido"); return; }
      const { data } = await api.post("/community-purchases/batch", {
        cooperado_id: batch.cooperado_id,
        nota_fiscal: batch.nota_fiscal,
        observacoes: batch.observacoes,
        forma_pagamento: batch.forma_pagamento,
        itens: cleanItens,
      });
      toast.success(`Compra ${data.numero} registrada · ${data.quantidade_itens} item(ns) · ${fmtBRL(data.total)}`);
      setOpen(false); setBatch(emptyBatch); load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const submitNewPerson = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/cooperados", personForm);
      toast.success(`${personForm.tipo === "cooperado" ? "Cooperado" : "Comunitário"} cadastrado`);
      // Recarrega lista e já seleciona
      const co = await api.get("/cooperados");
      setCooperados(co.data);
      setBatch((b) => ({ ...b, cooperado_id: data.id }));
      setNewPersonOpen(false);
      setPersonForm(emptyPerson);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const removeGroup = async (g) => {
    if (g.is_batch) {
      if (!confirm(`Excluir compra ${g.numero} com ${g.itens.length} item(ns) — ${fmtBRL(g.valor_total)}?\n\nATENÇÃO: o estoque de TODOS os itens será estornado.`)) return;
      try {
        await api.delete(`/community-purchases/batch/${g.batch_id}`);
        toast.success("Compra excluída · estoque estornado");
        load();
      } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    } else {
      const one = g.itens[0];
      if (!confirm(`Excluir compra ${g.numero}?\n\nATENÇÃO: o estoque do produto ${one.product_nome} será ESTORNADO (${one.quantidade} un.).`)) return;
      try {
        await api.delete(`/community-purchases/${one.id}`);
        toast.success("Compra excluída · estoque estornado");
        load();
      } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    }
  };

  const batchTotal = batch.itens.reduce((s, it) => s + (parseFloat(it.quantidade) || 0) * (parseFloat(it.valor_unitario) || 0), 0);

  const filteredGroups = groups.filter((g) => {
    if (listTipoFilter !== "all" && (g.cooperado_tipo || "cooperado") !== listTipoFilter) return false;
    if (!q) return true;
    const nq = normalize(q);
    if (normalize(g.numero).includes(nq)) return true;
    if (normalize(g.cooperado_nome).includes(nq)) return true;
    if (normalize(g.cooperado_funcao).includes(nq)) return true;
    if (normalize(g.community_nome).includes(nq)) return true;
    if (normalize(g.nota_fiscal).includes(nq)) return true;
    return (g.itens || []).some((it) => normalize(it.product_nome).includes(nq) || normalize(it.product_codigo).includes(nq));
  });


  const filteredPeople = cooperados.filter((c) => {
    if (personTipoFilter === "all") return true;
    return (c.tipo || "cooperado") === personTipoFilter;
  });

  const totalGeralFiltrado = filteredGroups.reduce((s, g) => s + (g.valor_total || 0), 0);

  return (
    <div data-testid="purchases-page">
      <SectionTitle action={<Btn onClick={() => { setBatch(emptyBatch); setOpen(true); }} data-testid="add-purchase-btn"><Plus className="w-4 h-4" />Nova Compra</Btn>}>
        Compras Comunitárias · <ShoppingBasket className="inline w-5 h-5 -mt-1" />
      </SectionTitle>

      {/* Filtro por tipo na lista + busca */}
      <div className="brutal-card-sm flex flex-wrap items-center gap-2 px-3 py-2 mb-4 bg-white">
        <SearchBar
          value={q}
          onChange={setQ}
          placeholder="Buscar por Nº, cooperado, comunidade, NF ou produto..."
          testid="cp-search"
        />
        <span className="font-mono-data text-[10px] uppercase tracking-widest text-[#555] mx-2">Mostrar:</span>
        <button onClick={() => setListTipoFilter("all")} data-testid="list-filter-all" className={`px-3 py-1 border-2 border-[#111] text-xs font-bold uppercase ${listTipoFilter === "all" ? "bg-[#111] text-white" : "bg-white"}`}>Todos</button>
        <button onClick={() => setListTipoFilter("cooperado")} data-testid="list-filter-cooperado" className={`px-3 py-1 border-2 border-[#111] text-xs font-bold uppercase ${listTipoFilter === "cooperado" ? "bg-[#3D6B57] text-white" : "bg-white"}`}>Cooperados</button>
        <button onClick={() => setListTipoFilter("comunitario")} data-testid="list-filter-comunitario" className={`px-3 py-1 border-2 border-[#111] text-xs font-bold uppercase ${listTipoFilter === "comunitario" ? "bg-[#FF4F00] text-white" : "bg-white"}`}>Comunitários</button>
        <div className="basis-full md:basis-auto md:ml-auto px-3 py-1 bg-[#3D6B57] text-white font-mono-data text-[11px] uppercase tracking-widest whitespace-nowrap flex items-center gap-2 border-2 border-[#111]">
          <span>{filteredGroups.length} {filteredGroups.length === 1 ? "compra" : "compras"}</span>
          <span className="opacity-50">·</span>
          <b className="text-base">{fmtBRL(totalGeralFiltrado)}</b>
        </div>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="brutal-table">
          <thead>
            <tr>
              <th className="w-8"></th>
              <th>Nº</th>
              <th>Data</th>
              <th>Tipo</th>
              <th>Nome</th>
              <th>Comunidade</th>
              <th className="text-right">Itens</th>
              <th className="text-right">Total</th>
              <th>NF</th>
              <th>Pagamento</th>
              <th className="w-24">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center py-8 text-[#777] font-mono-data">Carregando...</td></tr>}
            {!loading && filteredGroups.length === 0 && <EmptyState message={q || listTipoFilter !== "all" ? "Nenhum resultado para o filtro atual." : "Nenhuma compra registrada."} cols={10} />}
            {filteredGroups.map((g) => {
              const key = g.batch_id || `single-${g.itens[0]?.id}`;
              const isOpen = !!expanded[key];
              const isCom = g.cooperado_tipo === "comunitario";
              return (
                <React.Fragment key={key}>
                  <tr data-testid={`community-purchase-row-${g.batch_id || g.itens[0]?.id}`} className="cursor-pointer" onClick={() => setExpanded((e) => ({ ...e, [key]: !e[key] }))}>
                    <td className="text-center">{g.itens.length > 1 ? (isOpen ? <ChevronDown className="w-4 h-4 inline" /> : <ChevronRight className="w-4 h-4 inline" />) : ""}</td>
                    <td className="font-mono-data font-bold">{g.numero}</td>
                    <td className="font-mono-data text-xs">{fmtDate(g.data_compra)}</td>
                    <td>
                      {isCom
                        ? <span className="inline-block px-2 py-0.5 bg-[#FF4F00] text-white font-mono-data text-[10px] font-bold uppercase tracking-widest" data-testid={`tipo-badge-com-${g.batch_id || g.itens[0]?.id}`}>Comunitário</span>
                        : <span className="inline-block px-2 py-0.5 bg-[#3D6B57] text-white font-mono-data text-[10px] font-bold uppercase tracking-widest" data-testid={`tipo-badge-coop-${g.batch_id || g.itens[0]?.id}`}>Cooperado</span>}
                    </td>
                    <td>
                      <div className="font-medium">{g.cooperado_nome}</div>
                      {g.cooperado_funcao && <div className="font-mono-data text-xs text-[#777]">{g.cooperado_funcao}</div>}
                    </td>
                    <td>{g.community_nome ? <Badge variant="green">{g.community_nome}</Badge> : "—"}</td>
                    <td className="text-right"><Badge variant={g.is_batch ? "green" : "outline"}>{g.itens.length} item{g.itens.length > 1 ? "s" : ""}</Badge></td>
                    <td className="text-right font-mono-data font-bold">{fmtBRL(g.valor_total)}</td>
                    <td className="font-mono-data text-xs">{g.nota_fiscal || "—"}</td>
                    <td className="font-mono-data text-xs">{g.forma_pagamento ? <Badge variant="outline">{(g.forma_pagamento || "").replace("_", " ")}</Badge> : "—"}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1">
                        {(() => {
                          const coopInfo = cooperados.find((c) => c.id === g.cooperado_id);
                          const phone = coopInfo?.telefone || "";
                          return (
                            <button
                              onClick={() => openWhatsapp(phone, formatCommunityPurchaseMessage(g))}
                              data-testid={`wa-community-${g.batch_id || g.itens[0].id}`}
                              className="p-1.5 hover:bg-[#25D366] hover:text-white border border-[#111] inline-flex"
                              title={phone ? `Enviar pelo WhatsApp para ${phone}` : "Compartilhar via WhatsApp (escolher contato)"}
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                            </button>
                          );
                        })()}
                        {g.is_batch ? (
                          <a href={`/imprimir/compra-comunidade-lote/${g.batch_id}`} target="_blank" rel="noreferrer" data-testid={`print-community-batch-${g.batch_id}`} className="p-1.5 hover:bg-[#3D6B57] hover:text-white border border-[#111] inline-flex" title="Imprimir recibo em lote"><Printer className="w-3.5 h-3.5" /></a>
                        ) : (
                          <a href={`/imprimir/compra-comunidade/${g.itens[0].id}`} target="_blank" rel="noreferrer" data-testid={`print-community-${g.itens[0].id}`} className="p-1.5 hover:bg-[#3D6B57] hover:text-white border border-[#111] inline-flex" title="Imprimir recibo"><Printer className="w-3.5 h-3.5" /></a>
                        )}
                        {canEdit && <button onClick={() => removeGroup(g)} data-testid={`delete-community-purchase-${g.batch_id || g.itens[0].id}`} className="p-1.5 hover:bg-[#E60000] hover:text-white border border-[#111]" title="Excluir · estorna estoque"><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                    </td>
                  </tr>
                  {isOpen && g.itens.length > 0 && (
                    <tr className="bg-[#FAFAFA]" data-testid={`community-items-detail-${g.batch_id || g.itens[0]?.id}`}>
                      <td colSpan={10} className="p-0">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-[#F0F7F2]">
                              <th className="text-left px-3 py-1 font-mono-data text-[10px] uppercase tracking-widest">Código</th>
                              <th className="text-left px-3 py-1 font-mono-data text-[10px] uppercase tracking-widest">Produto</th>
                              <th className="text-right px-3 py-1 font-mono-data text-[10px] uppercase tracking-widest">Qtd</th>
                              <th className="text-right px-3 py-1 font-mono-data text-[10px] uppercase tracking-widest">Vlr Un</th>
                              <th className="text-right px-3 py-1 font-mono-data text-[10px] uppercase tracking-widest">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.itens.map((it) => (
                              <tr key={it.id} data-testid={`community-item-${it.id}`}>
                                <td className="px-3 py-1 font-mono-data text-xs">{it.product_codigo}</td>
                                <td className="px-3 py-1">{it.product_nome}</td>
                                <td className="px-3 py-1 text-right font-mono-data">{it.quantidade}</td>
                                <td className="px-3 py-1 text-right font-mono-data">{fmtBRL(it.valor_unitario)}</td>
                                <td className="px-3 py-1 text-right font-mono-data font-bold">{fmtBRL(it.valor_total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MODAL Nova Compra */}
      <Modal open={open} onClose={() => setOpen(false)} title="Nova Compra Comunitária" size="xl">
        <form onSubmit={submit} className="space-y-4" data-testid="purchase-form">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="font-mono-data text-[10px] uppercase tracking-widest text-[#555]">Cooperado / Comunitário *</label>
              <button type="button" onClick={() => { setPersonForm({ ...emptyPerson, tipo: "comunitario" }); setNewPersonOpen(true); }} data-testid="open-new-person-btn" className="text-xs font-mono-data uppercase tracking-widest text-[#FF4F00] hover:underline inline-flex items-center gap-1">
                <UserPlus className="w-3.5 h-3.5" /> Cadastrar novo comunitário
              </button>
            </div>
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={() => setPersonTipoFilter("all")} data-testid="filter-tipo-all" className={`px-3 py-1 border-2 border-[#111] text-xs font-bold uppercase ${personTipoFilter === "all" ? "bg-[#111] text-white" : "bg-white"}`}>Todos</button>
              <button type="button" onClick={() => setPersonTipoFilter("cooperado")} data-testid="filter-tipo-cooperado" className={`px-3 py-1 border-2 border-[#111] text-xs font-bold uppercase ${personTipoFilter === "cooperado" ? "bg-[#111] text-white" : "bg-white"}`}>Cooperados</button>
              <button type="button" onClick={() => setPersonTipoFilter("comunitario")} data-testid="filter-tipo-comunitario" className={`px-3 py-1 border-2 border-[#111] text-xs font-bold uppercase ${personTipoFilter === "comunitario" ? "bg-[#111] text-white" : "bg-white"}`}>Comunitários</button>
            </div>
            <CooperadoCombobox
              items={cooperados}
              value={batch.cooperado_id}
              onChange={(id) => setBatch({ ...batch, cooperado_id: id })}
              tipoFilter={personTipoFilter}
              testid="cooperado-combobox"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Nota Fiscal / Recibo" value={batch.nota_fiscal} onChange={(v) => setBatch({ ...batch, nota_fiscal: v })} />
            <SelectField label="Forma de Pagamento" value={batch.forma_pagamento} onChange={(v) => setBatch({ ...batch, forma_pagamento: v })} options={[
              { value: "PIX", label: "PIX" },
              { value: "Transferencia", label: "Transferência" },
              { value: "Dinheiro", label: "Dinheiro" },
              { value: "Cheque", label: "Cheque" },
              { value: "Boleto", label: "Boleto" },
              { value: "Cartao", label: "Cartão" },
              { value: "A_Prazo", label: "A prazo" },
            ]} testid="cp-forma-pagamento" />
            <TextArea label="Observações" value={batch.observacoes} onChange={(v) => setBatch({ ...batch, observacoes: v })} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="font-mono-data text-[10px] uppercase tracking-widest text-[#3D6B57]">Itens da compra *</label>
              <button type="button" onClick={addItem} data-testid="add-community-item-btn" className="text-xs font-mono-data uppercase tracking-widest text-[#FF4F00] hover:underline">+ Adicionar item</button>
            </div>
            <div className="space-y-2" data-testid="community-items-list">
              {batch.itens.map((it, i) => (
                <div key={i} className="brutal-card-sm p-2 grid grid-cols-12 gap-2 items-center" data-testid={`community-item-row-${i}`}>
                  <div className="col-span-6">
                    <ProductCombobox
                      items={products}
                      value={it.product_id}
                      onChange={(id, prod) => {
                        setItem(i, "product_id", id);
                        // Autopreenche o valor unitário com o valor médio do produto, se vazio
                        if (prod && prod.valor_medio && (!it.valor_unitario || parseFloat(it.valor_unitario) === 0)) {
                          setItem(i, "valor_unitario", Number(prod.valor_medio).toFixed(2));
                        }
                      }}
                      testid={`community-item-product-${i}`}
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <input type="number" step="0.01" min="0.01" required value={it.quantidade} onChange={(e) => setItem(i, "quantidade", e.target.value)} placeholder="Qtd" className="w-full border-2 border-[#111] p-2 text-sm text-right font-mono-data" />
                  </div>
                  <div className="col-span-3">
                    <input type="number" step="0.01" min="0" required value={it.valor_unitario} onChange={(e) => setItem(i, "valor_unitario", e.target.value)} placeholder="Vlr un." className="w-full border-2 border-[#111] p-2 text-sm text-right font-mono-data" />
                  </div>
                  <div className="col-span-1 flex items-center justify-end gap-1">
                    <span className="font-mono-data text-xs font-bold whitespace-nowrap text-[#3D6B57]">{fmtBRL((parseFloat(it.quantidade) || 0) * (parseFloat(it.valor_unitario) || 0))}</span>
                    {batch.itens.length > 1 && (
                      <button type="button" onClick={() => removeItem(i)} data-testid={`remove-community-item-${i}`} className="p-1 border-2 border-[#111] hover:bg-[#E60000] hover:text-white">
                        <XIcon className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-[#FAFAFA] border-2 border-[#111] p-3 flex justify-between items-center">
            <span className="font-mono-data text-xs text-[#555]">Total da compra ({batch.itens.length} item{batch.itens.length > 1 ? "s" : ""})</span>
            <span className="font-display font-black text-2xl text-[#3D6B57]" data-testid="community-batch-total">{fmtBRL(batchTotal)}</span>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-purchase-btn">Registrar Compra</Btn>
          </div>
        </form>
      </Modal>

      {/* MODAL Cadastrar novo Cooperado / Comunitário */}
      <Modal open={newPersonOpen} onClose={() => setNewPersonOpen(false)} title="Cadastrar Cooperado / Comunitário" size="md">
        <form onSubmit={submitNewPerson} className="grid grid-cols-2 gap-4" data-testid="new-person-form">
          <div className="col-span-2 flex gap-2">
            <button type="button" onClick={() => setPersonForm({ ...personForm, tipo: "cooperado" })} data-testid="new-person-tipo-cooperado" className={`flex-1 px-3 py-2 border-2 border-[#111] text-xs font-bold uppercase ${personForm.tipo === "cooperado" ? "bg-[#111] text-white" : "bg-white"}`}>Cooperado</button>
            <button type="button" onClick={() => setPersonForm({ ...personForm, tipo: "comunitario" })} data-testid="new-person-tipo-comunitario" className={`flex-1 px-3 py-2 border-2 border-[#111] text-xs font-bold uppercase ${personForm.tipo === "comunitario" ? "bg-[#111] text-white" : "bg-white"}`}>Comunitário (não cooperado)</button>
          </div>
          <Field label="Nome *" required value={personForm.nome} onChange={(v) => setPersonForm({ ...personForm, nome: v })} colspan testid="new-person-nome" />
          <Field label="CPF" value={personForm.cpf} onChange={(v) => setPersonForm({ ...personForm, cpf: v })} />
          <Field label="Telefone" value={personForm.telefone} onChange={(v) => setPersonForm({ ...personForm, telefone: v })} />
          <SelectField label="Comunidade" value={personForm.community_id} onChange={(v) => setPersonForm({ ...personForm, community_id: v })} options={communities.map((c) => ({ value: c.id, label: c.nome }))} colspan />
          <Field label="Função / Setor (opcional)" value={personForm.funcao} onChange={(v) => setPersonForm({ ...personForm, funcao: v })} placeholder="Ex.: Motosserrista, Comunitário, Diarista..." colspan />
          <div className="col-span-2 flex justify-end gap-2 pt-2 border-t-2 border-[#111]">
            <Btn type="button" variant="secondary" onClick={() => setNewPersonOpen(false)}>Cancelar</Btn>
            <Btn type="submit" data-testid="save-new-person-btn">Cadastrar e selecionar</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
